import hoistNonReactStatics from 'hoist-non-react-statics';
import { flowRight, pick } from 'lodash/fp';
import { Component, createElement } from 'react';

export const shallowEqual = (first, second) => {
    if (Object.is(first, second)) {
        return true;
    }

    if (first === null || second === null || typeof first !== 'object' || typeof second !== 'object') {
        return false;
    }

    const firstKeys = Object.keys(first);
    const secondKeys = Object.keys(second);

    if (firstKeys.length !== secondKeys.length) {
        return false;
    }

    for (const key of firstKeys) {
        if (!Object.prototype.hasOwnProperty.call(second, key) || !Object.is(first[key], second[key])) {
            return false;
        }
    }

    return true;
};

const setDisplayName = displayName => BaseComponent => {
    BaseComponent.displayName = displayName;
    return BaseComponent;
};

export const wrapDisplayName = (BaseComponent, hocName) => {
    const baseName = BaseComponent.displayName || BaseComponent.name || 'Component';
    return `${hocName}(${baseName})`;
};

export const compose = (...funcs) => flowRight(...funcs);

export const hoistStatics = hoc => BaseComponent => hoistNonReactStatics(hoc(BaseComponent), BaseComponent);

export const setPropTypes = propTypes => BaseComponent => {
    BaseComponent.propTypes = {
        ...BaseComponent.propTypes,
        ...propTypes,
    };
    return BaseComponent;
};

export const mapProps = propsMapper => BaseComponent => {
    const MapProps = props => createElement(BaseComponent, propsMapper(props));
    if (process.env.NODE_ENV !== 'production') {
        return setDisplayName(wrapDisplayName(BaseComponent, 'mapProps'))(MapProps);
    }
    return MapProps;
};

export const shouldUpdate = test => BaseComponent => {
    class ShouldUpdate extends Component {
        shouldComponentUpdate(nextProps) {
            return test(this.props, nextProps);
        }

        render() {
            return createElement(BaseComponent, this.props);
        }
    }

    if (process.env.NODE_ENV !== 'production') {
        return setDisplayName(wrapDisplayName(BaseComponent, 'shouldUpdate'))(ShouldUpdate);
    }
    return ShouldUpdate;
};

export const onlyUpdateForKeys = propKeys => {
    const pickProps = pick(propKeys);
    const hoc = shouldUpdate((props, nextProps) => !shallowEqual(pickProps(nextProps), pickProps(props)));

    if (process.env.NODE_ENV !== 'production') {
        return BaseComponent => setDisplayName(wrapDisplayName(BaseComponent, 'onlyUpdateForKeys'))(hoc(BaseComponent));
    }
    return hoc;
};

export const pure = BaseComponent => {
    const hoc = shouldUpdate((props, nextProps) => !shallowEqual(props, nextProps));

    if (process.env.NODE_ENV !== 'production') {
        return setDisplayName(wrapDisplayName(BaseComponent, 'pure'))(hoc(BaseComponent));
    }

    return hoc(BaseComponent);
};
